import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkles, ChevronLeft, Upload, X } from "lucide-react";

/**
 * Signup — self-service registration for klien (Item 6 iter v18).
 *
 * Flow:
 *  1) Client fills form (name, email, password, company, address, NPWP, phone, optional photo).
 *  2) If company already exists in master DB, we won't duplicate.
 *  3) Email verification link sent via Resend.
 *  4) After email confirmed, admin approves → user can login.
 */
export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    client_name: "", address: "", npwp: "", phone: "",
    photo_b64: "",
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Pull master client list to help the user pick existing perusahaan (optional)
  useEffect(() => {
    api.get("/clients/public-names").then((r) => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  const existingClient = useMemo(() => {
    const cn = form.client_name.trim().toLowerCase();
    if (!cn) return null;
    return clients.find((c) => (c.name || "").toLowerCase() === cn) || null;
  }, [form.client_name, clients]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Foto maksimal 2 MB"); return; }
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || "");
      const b64 = dataUrl.split(",")[1] || "";
      setForm((s) => ({ ...s, photo_b64: b64 }));
    };
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Kata sandi minimal 6 karakter"); return; }
    setLoading(true);
    try {
      await api.post("/auth/signup", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mendaftar");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#f6f5f0] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border border-stone-200/70 rounded-2xl p-8 text-center" data-testid="signup-done">
          <Sparkles className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">Cek Inbox Anda</h1>
          <p className="text-sm text-stone-600 leading-relaxed">
            Kami telah mengirim email konfirmasi ke <span className="font-mono text-stone-900">{form.email}</span>. Mohon klik tautan di email untuk verifikasi.
          </p>
          <p className="text-xs text-stone-400 mt-3">Tidak menerima email dalam beberapa menit? Cek folder spam atau coba daftar ulang.</p>
          <button onClick={() => navigate("/login")} className="mt-6 text-xs uppercase tracking-wider text-blue-700 hover:text-blue-900 hover:underline" data-testid="signup-done-back-login">
            ← Kembali ke Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f5f0] flex items-center justify-center p-4 py-12">
      <div className="max-w-2xl w-full">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-stone-500 hover:text-blue-700 mb-4">
          <ChevronLeft className="h-3 w-3" /> Kembali ke Login
        </Link>
        <div className="bg-white border border-stone-200 shadow-sm">
          <div className="p-6 md:p-8 border-b border-stone-100">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-terra-700 mb-1">
              <Sparkles className="h-3 w-3" /> Daftar Akun Klien
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-900">Mulai akses katalog & pantau pesanan Anda</h1>
            <p className="text-sm text-stone-500 mt-1">Setelah email dikonfirmasi, admin akan meninjau pendaftaran Anda.</p>
          </div>
          <form onSubmit={submit} className="p-6 md:p-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nama Lengkap" required>
                <input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="signup-name" />
              </Field>
              <Field label="Email" required hint="Wajib aktif — untuk konfirmasi & reset password">
                <input type="email" className="inp" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="signup-email" />
              </Field>
              <Field label="Kata Sandi" required hint="Minimal 6 karakter">
                <input type="password" className="inp" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} required data-testid="signup-password" />
              </Field>
              <Field label="No. Telepon">
                <input className="inp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="signup-phone" />
              </Field>
              <Field label="Nama Perusahaan / Klien" required hint={existingClient ? `✓ Sudah terdaftar di database (${existingClient.name})` : "Jika belum terdaftar, akan ditambahkan otomatis setelah admin approve"}>
                <input className="inp" list="client-suggest" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required data-testid="signup-client-name" />
                <datalist id="client-suggest">
                  {clients.map((c) => <option key={c.name} value={c.name} />)}
                </datalist>
              </Field>
              <Field label="NPWP" hint="Format 00.000.000.0-000.000">
                <input className="inp" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} data-testid="signup-npwp" />
              </Field>
              <Field label="Alamat (sesuai NPWP)" className="md:col-span-2">
                <textarea className="inp" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="signup-address" />
              </Field>
              <Field label="Foto Profil (opsional)" hint="Max 2 MB. PNG/JPG." className="md:col-span-2">
                <div className="flex items-center gap-3">
                  {form.photo_b64 ? (
                    <div className="relative h-16 w-16 border border-stone-200 overflow-hidden">
                      <img src={`data:image/png;base64,${form.photo_b64}`} alt="profile" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => setForm({ ...form, photo_b64: "" })} className="absolute top-0 right-0 bg-red-600 text-white p-0.5" data-testid="signup-photo-remove">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex items-center gap-2 text-xs text-stone-600 hover:text-terra-700 border border-dashed border-stone-300 px-3 py-2" data-testid="signup-photo-upload">
                      <Upload className="h-3.5 w-3.5" />
                      Pilih foto
                      <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                    </label>
                  )}
                </div>
              </Field>
            </div>
            <div className="text-[11px] text-stone-500 leading-relaxed bg-stone-50 border border-stone-200 p-3">
              Dengan mendaftar, Anda setuju bahwa data yang Anda masukkan akan ditinjau oleh admin sebelum akun diaktifkan. Email Anda hanya akan digunakan untuk komunikasi terkait pesanan & akun.
            </div>
            <button type="submit" disabled={loading} className="w-full bg-stone-900 hover:bg-blue-700 disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium tracking-wide" data-testid="signup-submit">
              {loading ? "Mengirim…" : "Daftar & Kirim Email Konfirmasi"}
            </button>
          </form>
        </div>
      </div>
      <style>{`.inp{ width:100%; border:1px solid #d6d3d1; padding:.55rem .75rem; font-size:.875rem; }
        .inp:focus{ border-color:#c54a34; outline: 1px solid #c54a34; }`}</style>
    </div>
  );
}

function Field({ label, required, hint, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-wider text-stone-600 font-semibold mb-1">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
      {hint && <p className="text-[10px] text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}
